import { useRequest, useMemoizedFn } from 'ahooks';
import { invalidateTags } from './registry';
import type { HttpError } from '../api/errors';
export interface UseMutationOptions<TData, TVars> {
  invalidate?: readonly string[];
  onSuccess?: (data: TData, vars: TVars) => void;
  onError?: (error: HttpError) => void;
}
export type UseMutationResult<TData, TVars> = {
  run: (vars: TVars) => Promise<TData>;
  fire: (vars: TVars) => void;
  loading: boolean;
};
export function useMutation<TData, TVars = void>(
  fetcher: (vars: TVars) => Promise<TData>,
  options: UseMutationOptions<TData, TVars> = {},
): UseMutationResult<TData, TVars> {
  const { invalidate = [], onSuccess, onError } = options;
  const { runAsync, loading } = useRequest(fetcher, { manual: true });
  const run = useMemoizedFn(async (vars: TVars): Promise<TData> => {
    try {
      const data = await runAsync(vars);
      onSuccess?.(data, vars);
      invalidateTags(invalidate);
      return data;
    } catch (error) {
      onError?.(error as HttpError);
      throw error;
    }
  });
  const fire = useMemoizedFn((vars: TVars): void => {
    void run(vars).catch(() => undefined);
  });
  return { run, fire, loading };
}
